// admin-client/src/lib/datetime.js
// =============================================================================
// Render every time in 24-hour format using the universal app timezone.
//
// The active timezone is set once by SettingsContext after /api/settings/public
// resolves. Until then we render in the browser's local tz (which is fine for
// the brief pre-login window).
// =============================================================================

let activeTz = null;
const pad = (n) => String(n).padStart(2, '0');

export function setActiveTimezone(tz) {
  activeTz = tz || null;
}

function asDate(d) {
  if (!d) return null;
  if (d instanceof Date) return Number.isNaN(d.getTime()) ? null : d;
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? null : x;
}

function partsIn(date, tz) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric',
    hour12: false,
  });
  const out = {};
  for (const p of fmt.formatToParts(date)) {
    if (p.type === 'literal') continue;
    out[p.type] = parseInt(p.value, 10);
  }
  if (out.hour === 24) out.hour = 0;
  return out;
}

function getParts(d, tz) {
  if (tz) return partsIn(d, tz);
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
    second: d.getSeconds(),
  };
}

// Return { date: 'YYYY-MM-DD', hour: int } for the given moment in the
// active app timezone. Used by Calendar.jsx so its cell-indexing keys
// match the timezone the server stores in (browser-local would shift
// hours and could roll dates across midnight).
export function partsInActiveTz(d) {
  const x = asDate(d);
  if (!x) return null;
  const p = getParts(x, activeTz);
  return { date: `${p.year}-${pad(p.month)}-${pad(p.day)}`, hour: p.hour };
}

export function fmtDate(d, tz = activeTz) {
  const x = asDate(d);
  if (!x) return '';
  const p = getParts(x, tz);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

export function fmtTime(d, tz = activeTz) {
  const x = asDate(d);
  if (!x) return '';
  const p = getParts(x, tz);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

export function fmtDateTime(d, tz = activeTz) {
  const x = asDate(d);
  if (!x) return '';
  return `${fmtDate(x, tz)} ${fmtTime(x, tz)}`;
}

// HTML datetime-local input value (YYYY-MM-DDTHH:mm) using the browser's
// local time. The input itself is browser-local — we don't try to fight that.
export function toDtLocalInput(d) {
  const x = asDate(d);
  if (!x) return '';
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
}

export function fromDtLocalInput(s) {
  if (!s) return null;
  const x = new Date(s);
  return Number.isNaN(x.getTime()) ? null : x;
}
